import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ZIP_INDEX from '../data/zip_indexs.json'
import AddressAutocomplete from '../components/AddressAutocomplete';
import { EMAIL_IN_STREET_ERROR, looksLikeEmail } from '../utils/address';

const FIELD_LABELS = {
  name: 'Business name',
  business_type: 'Business type',
  EIN: 'EIN',
  street_address: 'Street address',
  zip_code: 'ZIP code',
  country: 'Country',
  city: 'City',
  state: 'State',
  signing_name: 'Signing authority name',
  signing_pin: 'Signing PIN',
  signing_phone: 'Signing phone',
  signing_job_title: 'Signing job title',
  signing_ssn: 'Signing SSN',
};

function formatApiError(data) {
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  const messages = [];
  for (const [key, val] of Object.entries(data)) {
    const arr = Array.isArray(val) ? val : [val];
    const label = key === 'non_field_errors' ? '' : `${FIELD_LABELS[key] || key}: `;
    for (const m of arr) {
      if (typeof m === 'string') messages.push(`${label}${m}`);
    }
  }
  return messages.length ? messages.join(' ') : null;
}

function EditCompany() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState({
    name: '',
    business_type: 'LLC',
    EIN: '',
    street_address: '',
    zip_code: '',
    country: '',
    city: '',
    state: '',
    signing_name: '',
    signing_pin: '',
    signing_phone: '',
    signing_job_title: '',
    third_party_allowed: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [streetError, setStreetError] = useState('');
  const [saving, setSaving] = useState(false);

  // USPS address validation
  const [uspsValidating, setUspsValidating] = useState(false);
  const [uspsSuggestion, setUspsSuggestion] = useState(null);
  const [uspsNotFound, setUspsNotFound] = useState(false);
  const [uspsUnavailable, setUspsUnavailable] = useState(false);
  const [uspsAptInvalid, setUspsAptInvalid] = useState(false);
  const [uspsChecked, setUspsChecked] = useState(false);
  const zip5 = (v) => String(v ?? '').replace(/\D/g, '').slice(0, 5); // берём первые 5 цифр
  const norm = (s) =>
    String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\./g, '');
  const streetRegex = /^[A-Za-z0-9]( ?[A-Za-z0-9\-\/])*$/;
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/companies/${id}/`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Ошибка при загрузке данных компании');
        const data = await response.json();
        setCompanyData({
          name: data.name || '',
          business_type: data.business_type || 'LLC',
          EIN: data.EIN || '',
          street_address: data.street_address || '',
          zip_code: data.zip_code || '',
          country: data.country || '',
          city: data.city || '',
          state: data.state || '',
          signing_name: data.signing_name || '',
          signing_pin: data.signing_pin || '',
          signing_phone: data.signing_phone || '',
          signing_job_title: data.signing_job_title || '',
          third_party_allowed: data.third_party_allowed || false,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);
  const sanitizeStreet = (value) => {
    let filtered = String(value ?? '').replace(/[^A-Za-z0-9\-\/ ]/g, '');
    if (filtered && !/^[A-Za-z0-9]/.test(filtered[0])) {
      filtered = filtered.slice(1);
    }
    return filtered;
  };

  const resetUsps = () => {
    setUspsChecked(false);
    setUspsSuggestion(null);
    setUspsNotFound(false);
    setUspsUnavailable(false);
    setUspsAptInvalid(false);
  };

  // Полная нормализованная строка адреса от USPS, включая apt/ste/unit, если он есть.
  // Предпочитаем streetAddressAbbreviation (USPS уже всё склеил), иначе руками
  // соединяем primary + secondary.
  const uspsFullStreet = (addr) => {
    if (!addr) return '';
    if (addr.streetAddressAbbreviation) return String(addr.streetAddressAbbreviation);
    const primary = addr.streetAddress || '';
    const secondary = addr.secondaryAddress || '';
    return [primary, secondary].filter(Boolean).join(' ').trim();
  };

  const handleStreetChange = (e) => {
    // Email не превращаем в «адрес» вырезанием символов — останавливаем и объясняем
    if (looksLikeEmail(e.target.value)) {
      setStreetError(EMAIL_IN_STREET_ERROR);
      return;
    }
    if (streetError) setStreetError('');
    const clean = sanitizeStreet(e.target.value).slice(0, 35);
    setCompanyData(prev => ({ ...prev, street_address: clean }));
    if (error) setError(null);
    resetUsps();
  };

  const handleAddressSelected = ({ streetAddress, city, state, zip }) => {
    // Юзер только что выбрал подсказку из Google — всегда коммитим распарсенные
    // поля, не падая обратно на prev (туда onChange успел записать
    // formatted_address до того, как сработал place_changed).
    setCompanyData(prev => ({
      ...prev,
      street_address: sanitizeStreet(streetAddress || '').slice(0, 35),
      city: city || '',
      state: state || '',
      zip_code: zip || '',
    }));
    if (error) setError(null);
    resetUsps();
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (['city', 'state', 'zip_code'].includes(name)) resetUsps();
  };

  const uspsAddrDiffers = (suggestion) => {
    const n = (s) => (s || '').trim().toUpperCase();
    return (
      n(uspsFullStreet(suggestion)) !== n(companyData.street_address) ||
      n(suggestion.city) !== n(companyData.city) ||
      n(suggestion.state) !== n(companyData.state) ||
      n(suggestion.ZIPCode) !== n(zip5(companyData.zip_code))
    );
  };

  const applyUspsSuggestion = () => {
    if (!uspsSuggestion) return;
    const fullStreet = uspsFullStreet(uspsSuggestion);
    const overrides = {
      street_address: fullStreet || companyData.street_address,
      city: uspsSuggestion.city || companyData.city,
      state: uspsSuggestion.state || companyData.state,
      zip_code: uspsSuggestion.ZIPCode || companyData.zip_code,
    };
    setCompanyData(prev => ({ ...prev, ...overrides }));
    setUspsSuggestion(null);
    setUspsNotFound(false);
    setUspsAptInvalid(false);
    setUspsChecked(true);
    return overrides;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const street = String(companyData.street_address || '').trim();
    if (!street) {
      setError('Street address is required.');
      setSaving(false);
      return;
    }
    if (!streetRegex.test(street)) {
      setError('Invalid street address (letters/digits, single spaces, "-" and "/" only; must start with a letter or digit).');
      setSaving(false);
      return;
    }
    // --- ZIP → city/state валидация ---
    const z = zip5(companyData.zip_code);
    if (!z || z.length !== 5) {
      setError('Enter 5-digit ZIP (or ZIP+4).');
      setSaving(false);
      return;
    }

    const ref = ZIP_INDEX[z];
    if (!ref) {
      setError('ZIP code not found in reference.');
      setSaving(false);
      return;
    }
    if (ref.state.toUpperCase() !== String(companyData.state).trim().toUpperCase()) {
      setError(`State does not match the ZIP (${z}). Expected ${ref.state}.`);
      setSaving(false);
      return;
    }
    if (norm(ref.city) !== norm(companyData.city)) {
      setError(`City does not match the ZIP (${z}). Expected ${ref.city}.`);
      setSaving(false);
      return;
    }
    // Signing PIN: required, 5 digits, not all zeros (IRS rejects an all-zeros
    // Taxpayer PIN — rule R0000-031; an empty PIN also becomes 00000).
    const pinVal = String(companyData.signing_pin || '').trim();
    if (!pinVal || !/^\d{5}$/.test(pinVal) || /^0+$/.test(pinVal)) {
      setError(
        !pinVal
          ? 'Signing PIN is required.'
          : !/^\d{5}$/.test(pinVal)
            ? 'Signing PIN must be 5 digits.'
            : 'Signing PIN cannot be all zeros.'
      );
      setSaving(false);
      return;
    }
    // --- конец локальной валидации ---

    // USPS address check (skip if already confirmed by user)
    if (!uspsChecked) {
      setUspsValidating(true);
      setUspsSuggestion(null);
      setUspsNotFound(false);
      setUspsUnavailable(false);
      setUspsAptInvalid(false);
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch('/api/validate-address/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            streetAddress: street,
            city: String(companyData.city || '').trim(),
            state: String(companyData.state || '').trim(),
            ZIPCode: z,
          }),
        });
        if (resp.ok) {
          const data = await resp.json();
          const addr = data?.address || {};
          const dpv = data?.additionalInfo?.DPVConfirmation || data?.additionalInfo?.dpvConfirmation || '';
          // DPV: Y = primary+secondary OK, S = primary OK but secondary (apt) not confirmed,
          //      D = secondary missing where required, N = primary not found.
          if (dpv === 'N') {
            setUspsNotFound(true);
            setUspsValidating(false);
            setSaving(false);
            return;
          }
          if (dpv === 'S' || dpv === 'D') {
            setUspsAptInvalid(true);
            setUspsValidating(false);
            setSaving(false);
            return;
          }
          if (addr.streetAddress && uspsAddrDiffers(addr)) {
            setUspsSuggestion(addr);
            setUspsValidating(false);
            setSaving(false);
            return;
          }
          // Address confirmed by USPS — proceed to save
        } else if (resp.status === 404) {
          // USPS: address not found
          setUspsNotFound(true);
          setUspsValidating(false);
          setSaving(false);
          return;
        } else if (resp.status === 503) {
          // USPS service temporarily unavailable — silently skip
        } else {
          // Other backend error — warn user
          setUspsUnavailable(true);
          setUspsValidating(false);
          setSaving(false);
          return;
        }
      } catch (_) {
        // Network error — silently skip
      } finally {
        setUspsValidating(false);
      }
    }

    await doSave();
  };

  const doSave = async (dataOverrides = {}) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/companies/${id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ...companyData, ...dataOverrides }),
      });
      if (!resp.ok) {
        let data = null;
        try { data = await resp.json(); } catch (_) { /* no JSON body */ }
        throw new Error(formatApiError(data) || 'Failed to save changes.');
      }
      navigate(`/company/${id}`);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return <div className="text-center text-gray-700 text-lg">Loading...</div>;
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="bg-[#0F2C5E] text-white p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <img src="/logo.png" alt="Logo" className="h-8 w-8" />
          <span className="font-bold text-xl">2290Pay.org</span>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem('token');
            window.location.href = '/sign-in';
          }}
          className="bg-white text-[#0F2C5E] px-4 py-2 rounded hover:bg-gray-200 transition"
        >
          Log out
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 bg-[#E4F2FF] p-4">
        <div className="max-w-5xl mx-auto bg-white rounded shadow p-6">
          <h1 className="text-2xl font-bold text-[#0F2C5E] mb-4">Edit Company Information</h1>
          {error && (
            <div className="mb-4 text-red-500">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-semibold mb-1" htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                type="text"
                value={companyData.name}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="business_type">Business Type</label>
              <select
                id="business_type"
                name="business_type"
                value={companyData.business_type}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              >
                <option value="LLC">LLC</option>
                <option value="Corporation">Corporation</option>
                <option value="Sole Proprietor">Sole Proprietor</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="EIN">EIN</label>
              <input
                id="EIN"
                name="EIN"
                type="text"
                value={companyData.EIN}
                onChange={handleChange}
                className="w-full border rounded p-2"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="street_address">Street Address</label>
              <AddressAutocomplete
                value={companyData.street_address}
                onChange={handleStreetChange}
                onAddressSelected={handleAddressSelected}
                inputProps={{
                  id: 'street_address',
                  name: 'street_address',
                  maxLength: 35,
                  required: true,
                  pattern: '[A-Za-z0-9]( ?[A-Za-z0-9\\-/])*',
                  title: 'Allowed: letters/digits, single spaces, "-" and "/" only; must start with a letter or digit',
                  className: 'w-full border rounded p-2',
                }}
              />
              {streetError && (
                <p className="text-red-500 text-sm mt-1" role="alert">{streetError}</p>
              )}
            </div>
            <div>
              <label className="block font-semibold mb-1" htmlFor="city">City</label>
              <input
                id="city"
                name="city"
                type="text"
                value={companyData.city}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>
            <div>
              <label className="block font-semibold mb-1" htmlFor="state">State</label>
              <input
                id="state"
                name="state"
                type="text"
                value={companyData.state}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>
            <div>
              <label className="block font-semibold mb-1" htmlFor="zip_code">Zip Code</label>
              <input
                id="zip_code"
                name="zip_code"
                type="text"
                value={companyData.zip_code}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="country">Country</label>
              <input
                id="country"
                name="country"
                type="text"
                value={companyData.country}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>





            <h2 className="text-xl font-bold text-[#0F2C5E] mt-6 mb-2">Signing Information</h2>

            <div>
              <label className="block font-semibold mb-1" htmlFor="signing_name">Signing Name</label>
              <input
                id="signing_name"
                name="signing_name"
                type="text"
                value={companyData.signing_name}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="signing_pin">Signing PIN</label>
              <input
                id="signing_pin"
                name="signing_pin"
                type="text"
                value={companyData.signing_pin}
                onChange={handleChange}
                className="w-full border rounded p-2"
              />
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="signing_phone">Signing Phone</label>
              <input
                id="signing_phone"
                name="signing_phone"
                type="text"
                value={companyData.signing_phone}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>

            <div>
              <label className="block font-semibold mb-1" htmlFor="signing_job_title">Signing Job Title</label>
              <input
                id="signing_job_title"
                name="signing_job_title"
                type="text"
                value={companyData.signing_job_title}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                id="third_party_allowed"
                name="third_party_allowed"
                type="checkbox"
                checked={companyData.third_party_allowed}
                onChange={handleChange}
                className="mr-2"
              />
              <label htmlFor="third_party_allowed" className="font-semibold">
                Third Party Designee Allowed
              </label>
            </div>

            {/* USPS address suggestion banner */}
            {uspsSuggestion && (
              <div className="p-3 bg-blue-50 border border-blue-300 rounded text-sm">
                <p className="font-semibold text-blue-800 mb-1">USPS suggests a corrected address:</p>
                <p className="text-blue-700 mb-2">
                  {uspsFullStreet(uspsSuggestion)}, {uspsSuggestion.city}, {uspsSuggestion.state} {uspsSuggestion.ZIPCode}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { const overrides = applyUspsSuggestion(); doSave(overrides); }}
                    className="bg-blue-700 text-white px-3 py-1 text-xs rounded hover:bg-blue-800 transition"
                  >
                    Use USPS Address &amp; Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setUspsSuggestion(null); doSave(); }}
                    className="bg-gray-600 text-white px-3 py-1 text-xs rounded hover:bg-gray-700 transition"
                  >
                    Keep My Address &amp; Save
                  </button>
                </div>
              </div>
            )}

            {/* USPS apartment/suite not confirmed banner */}
            {uspsAptInvalid && (
              <div className="p-3 bg-orange-50 border border-orange-300 rounded text-sm">
                <p className="font-semibold text-orange-800 mb-1">USPS could not confirm the apartment/suite number.</p>
                <p className="text-orange-700 mb-2">
                  The street address is valid, but the apartment, suite, or unit number you entered
                  was not recognized at this address. Double-check it before saving.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setUspsAptInvalid(false); doSave(); }}
                    className="bg-orange-600 text-white px-3 py-1 text-xs rounded hover:bg-orange-700 transition"
                  >
                    Save Anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => setUspsAptInvalid(false)}
                    className="bg-gray-500 text-white px-3 py-1 text-xs rounded hover:bg-gray-600 transition"
                  >
                    Edit Address
                  </button>
                </div>
              </div>
            )}

            {/* USPS service unavailable banner */}
            {uspsUnavailable && (
              <div className="p-3 bg-gray-50 border border-gray-300 rounded text-sm">
                <p className="font-semibold text-gray-700 mb-1">USPS address verification is currently unavailable.</p>
                <p className="text-gray-600 mb-2">Please double-check your address manually before saving.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setUspsUnavailable(false); doSave(); }}
                    className="bg-gray-700 text-white px-3 py-1 text-xs rounded hover:bg-gray-800 transition"
                  >
                    Save Anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => setUspsUnavailable(false)}
                    className="bg-gray-400 text-white px-3 py-1 text-xs rounded hover:bg-gray-500 transition"
                  >
                    Edit Address
                  </button>
                </div>
              </div>
            )}

            {/* USPS address not found banner */}
            {uspsNotFound && (
              <div className="p-3 bg-yellow-50 border border-yellow-400 rounded text-sm">
                <p className="font-semibold text-yellow-800 mb-1">USPS could not verify this address.</p>
                <p className="text-yellow-700 mb-2">The address may still be valid. You can save it as entered.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setUspsNotFound(false); doSave(); }}
                    className="bg-yellow-600 text-white px-3 py-1 text-xs rounded hover:bg-yellow-700 transition"
                  >
                    Save Anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => setUspsNotFound(false)}
                    className="bg-gray-500 text-white px-3 py-1 text-xs rounded hover:bg-gray-600 transition"
                  >
                    Edit Address
                  </button>
                </div>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-[#0F2C5E] text-white px-4 py-2 rounded hover:bg-blue-900 transition"
                disabled={saving || uspsValidating}
              >
                {uspsValidating ? 'Verifying address...' : saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
                onClick={() => navigate(`/company/${id}`)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F2C5E] text-white p-4">
        <div className="max-w-6xl mx-auto grid grid-cols-4 gap-4">
          <div>
            <h3 className="font-bold">2290PAY.ORG</h3>
          </div>
          <div>
            <h3 className="font-bold">Contact US</h3>
            <ul>
              <li>2290help@gmail.com</li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold">Our Services</h3>
            <ul>
              <li>2290Filing.com</li>
              <li>Efile2290.com</li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold">Fast Links</h3>
            <ul>
              <li>2290 VIN Correction</li>
              <li>Form 2290 Online</li>
              <li>Efile 2290 Tax Online</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default EditCompany;
